import React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import { getApplicationEndpoint } from '@gitbook/env';
import type { HiveWorkingBranch, HiveWorkingDocument } from '@gitbook/hive-client';
import { useHiveClient } from '@gitbook/hive-react';
import { createPalette, type PaletteCommandDef, type PaletteGroupDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { clipboard, toast } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { useNavigation } from '../../navigation';
import { apiClient } from '../../state';

/**
 * A group of commands to perform actions on an existing change request (archive, convert to draft).
 */
export function useChangeRequestActionsCommands(props: {
    spaceKey: string;
    changeRequest: api.ChangeRequest;
}): PaletteGroupDef | null {
    const { spaceKey, changeRequest } = props;

    const hiveClient = useHiveClient();

    const isAffectedSync = React.useCallback(
        (sync: HiveWorkingBranch | HiveWorkingDocument) => {
            return (
                sync.contentId === spaceKey &&
                sync.branchId === getChangeRequestBranchId(changeRequest.id)
            );
        },
        [spaceKey, changeRequest.id]
    );

    /** User confirmed that the change request should be archived */
    const archive = React.useCallback(async () => {
        await toast.catch(async () => {
            await hiveClient.sync.withReadOnlyDataSyncs(async () => {
                await apiClient.changeRequests.updateChangeRequestById(spaceKey, changeRequest.id, {
                    status: 'archived',
                });
            }, isAffectedSync);

            toast('Change request archived', {
                description: 'You can reopen this from the change requests panel at any time',
                icon: Icon.ChangeRequestArchive,
            });
        }, 'Error while archiving change request');
    }, [hiveClient, spaceKey, changeRequest.id, isAffectedSync]);

    const convertToDraft = React.useCallback(async () => {
        await hiveClient.sync.withReadOnlyDataSyncs(async () => {
            await toast.catch(async () => {
                await apiClient.changeRequests.updateChangeRequestById(spaceKey, changeRequest.id, {
                    status: 'draft',
                });

                toast('Change request reverted to draft', {
                    description:
                        'Continue editing this change request. You can submit it again later.',
                    icon: Icon.ChangeRequestDraft,
                });
            }, 'Error while converting change request to draft');
        }, isAffectedSync);
    }, [hiveClient, spaceKey, changeRequest.id, isAffectedSync]);

    return React.useMemo(() => {
        const group = {
            key: 'changeRequest.actions',
            commands: [
                changeRequest.status === api.ChangeRequestStatus.Open
                    ? {
                          key: 'changeRequest.draft',
                          icon: Icon.ChangeRequestDraft,
                          title: 'Convert to draft',
                          trigger: convertToDraft,
                      }
                    : null,
                [api.ChangeRequestStatus.Open, api.ChangeRequestStatus.Draft].includes(
                    changeRequest.status
                )
                    ? {
                          key: 'changeRequest.archive',
                          icon: Icon.Archive,
                          title: 'Archive',
                          trigger: archive,
                      }
                    : null,
            ].filter(filterOutNullable),
        };

        if (group.commands.length === 0) {
            return null;
        }

        return group;
    }, [changeRequest.status, archive, convertToDraft]);
}

/**
 * A group of commands to navigate common change request screens (comments, files, history, etc).
 */
export function useChangeRequestNavigationCommands(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
}): PaletteGroupDef {
    const { space, changeRequest } = props;
    const { navigate } = useNavigation();

    const paletteGroup = React.useMemo(() => {
        return {
            key: 'changeRequest.navigation',
            commands: [
                {
                    key: 'changeRequest.overview',
                    icon: Icon.InfoCircle,
                    title: 'Overview',
                    trigger: () => {
                        analytics.logEvent(
                            analytics.createChangeRequestEvent(space, changeRequest.id, {
                                type: 'change_request_details_panel_open',
                            })
                        );

                        navigate({
                            screen: {
                                sideSheet: { type: 'overview' },
                            },
                        });
                    },
                },

                // https://linear.app/gitbook-x/issue/RND-1988/explore-allowing-readers-to-access-history-and-change-requests
                space.permissions.edit
                    ? {
                          key: 'changeRequest.history',
                          icon: Icon.History,
                          title: 'Version history',
                          trigger: () => {
                              navigate({
                                  current: {
                                      revision: changeRequest.revision,
                                      mode: 'diff',
                                      sideSheet: undefined,
                                  },
                              });
                          },
                      }
                    : null,
                space.permissions.comment
                    ? {
                          key: 'changeRequest.comments',
                          icon: Icon.Comment,
                          title: 'Comments',
                          trigger: () => {
                              navigate({
                                  current: {
                                      sideSheet: {
                                          type: 'comments',
                                      },
                                  },
                              });
                          },
                      }
                    : null,
            ].filter(filterOutNullable),
        };
    }, [space, changeRequest.id, changeRequest.revision, navigate]);

    return paletteGroup;
}

/**
 * Return change request Copy commands.
 *
 * Mainly useful for development purposes.
 */
export function useChangeRequestCopyCommands(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
}): PaletteCommandDef {
    const { space, changeRequest } = props;
    const { getNavigationURL } = useNavigation();

    return React.useMemo(() => {
        return {
            key: 'changeRequest.copy',
            icon: Icon.Copy,
            title: 'Copy',
            descendant: () => {
                return createPalette({
                    static: true,
                    groups: [
                        {
                            key: 'changeRequest.copy',
                            commands: [
                                {
                                    key: 'changeRequest.copyLink',
                                    icon: Icon.Link,
                                    title: 'Copy link',
                                    trigger: () => {
                                        clipboard.writeText(
                                            `${getApplicationEndpoint()}${getNavigationURL({
                                                screen: {
                                                    type: 'space',
                                                    organization: space.organization,
                                                    space: space.id,
                                                    changeRequest: changeRequest.id,
                                                },
                                            })}`
                                        );
                                    },
                                },
                                {
                                    key: 'changeRequest.copyId',
                                    icon: Icon.ID,
                                    title: 'Copy ID',
                                    trigger: () => {
                                        clipboard.writeText(changeRequest.number.toString());
                                    },
                                },
                                {
                                    key: 'changeRequest.copyTitle',
                                    icon: Icon.Type,
                                    title: 'Copy title',
                                    trigger: () => {
                                        clipboard.writeText(changeRequest.subject);
                                    },
                                },
                            ],
                        },
                    ],
                });
            },
        };
    }, [space, changeRequest.id, changeRequest.number, changeRequest.subject, getNavigationURL]);
}
